import { useState, useEffect, useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";

import { Button } from "../../../components";
import { colors } from "../../../theme/colors";
import style from "./../specimen.module.css";
import { CustomTable } from "../../../components/DataTable";
import { ReactComponent as Basket } from "../../../assets/svg/basket.svg";
import { ReactComponent as Fridge } from "../../../assets/svg/fridge.svg";
import { ReactComponent as Freezer } from "../../../assets/svg/freezer.svg";
import { ReactComponent as Attachment } from "../../../assets/svg/attachments.svg";
import { PreviewDialog } from "../../../components/Dialog";
import { RootState, useAppSelector } from "../../../store";
import { formatDateTime, formatPersonName } from "../utils";

interface PatientSpecimenRelProps {
  details?: Partial<RequestFormResponse> & { hazard_bag?: HazardBag[] };
  isLoading?: boolean;
}

const Details = ({ details, isLoading }: PatientSpecimenRelProps) => {
  const navigate = useNavigate();
  const referenceData = useAppSelector((state: RootState) => state.referenceData);
  const procedureNameById = useMemo(
    () => new Map((referenceData?.procedures ?? []).map((procedure) => [procedure.id, procedure.name] as const)),
    [referenceData?.procedures]
  );
  const [specimens, setSpecimens] = useState<HazardBagSpecimen[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>();

  const getSpecimenName = (specimen: HazardBagSpecimen) => {
    const sutureTimeDetails =
      (specimen?.suture_time ? " " + specimen?.suture_time : "") +
      (specimen?.suture_time_edit ? `, ${specimen?.suture_time_edit}` : "");

    const selectedProcedureName = specimen?.procedure_id
      ? procedureNameById.get(specimen?.procedure_id)
      : undefined;

    const procedureDetails =
      (selectedProcedureName ?? "") +
      (specimen?.procedure_edit ? `, ${specimen?.procedure_edit}` : "");

    return `${procedureDetails}${sutureTimeDetails}, ${specimen?.organ ?? ""}${specimen?.site ? ", " + specimen?.site : ""}`;
  };

  const fields = [
    {
      id: "organ_detail",
      label: "Specimen",
      flex: 2.5,
      type: "name",
      render: (item: HazardBagSpecimen) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {getSpecimenName(item)}
        </Typography>
      ),
    },
    {
      id: "storage",
      label: "Storage",
      flex: 1,
      type: "name",
      render: (specimen: HazardBagSpecimen) =>
        ((specimen?.placed_in || "")?.toUpperCase() === "FREEZER" || (specimen?.specimen_storage || "")?.toUpperCase() === "FREEZER") ? (
          <Freezer width={34} height={34} />
        ) : ((specimen?.placed_in || "")?.toUpperCase() === "FRIDGE" || (specimen?.specimen_storage || "")?.toUpperCase() === "FRIDGE") ? (
          <Fridge width={34} height={34} />
        ) : (
          <Basket width={34} height={34} />
        ),
    },
    {
      id: "date",
      label: "Collection Date & Time",
      flex: 2,
      type: "name",
  render: (item: HazardBagSpecimen) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item?.created_at
            ? dayjs(item?.created_at).format("DD/MM/YYYY hh:mm A")
            : ""}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      type: "badge",
      flex: 2,
      render: (item: HazardBagSpecimen) =>
        (item?.specimen_status || "") !== "None" && item?.specimen_status ? (
          <Box
            className={style.detailsStatusContainer}
            sx={{
              backgroundColor:
                item?.specimen_status === "READY FOR PICKUP"
                  ? colors.lightGreen
                  : item?.specimen_status === "NO SPECIMEN"
                    ? colors.lightRed
                    : colors.modalBackground,
            }}
          >
            <Typography
              className={style.cell}
              sx={{
                fontSize: "12px",
                color: colors.black,
              }}
            >
              {item?.specimen_status !== "None" ? item?.specimen_status : ""}
            </Typography>
          </Box>
        ) : (
          <></>
        ),
    },
  ];

  useEffect(() => {
    if (!details) {
      setSpecimens([]);
      return;
    }
    // Flatten hazard bag specimens to a single list while preserving bag-level context such as status.
    const collected: HazardBagSpecimen[] = [];
    if (Array.isArray(details.hazard_bag)) {
      details.hazard_bag.forEach((bag) => {
        bag.specimens?.forEach((specimen) => {
          collected.push({
            ...specimen,
            organ_detail: specimen.organ || undefined,
            specimen_status: bag.status,
            // Keep placed_in: the storage-icon render (Freezer/Fridge/Basket)
            // reads placed_in OR specimen_storage. Nulling it here made the
            // placed_in branch dead, so a specimen whose storage arrives only
            // as placed_in always fell through to Basket.
          });
        });
      });
    }
      setSpecimens(collected);
  }, [details, details?.hazard_bag]);

  const hazardBagNotes: HazardBagNote[] = useMemo(() => {
    const list: HazardBagNote[] = [];
    if (Array.isArray(details?.hazard_bag)) {
      details?.hazard_bag.forEach((bag) => {
        if (Array.isArray(bag?.notes) && bag.notes.length) {
          list.push(...bag.notes);
        }
      });
    }
    return list;
  }, [details?.hazard_bag]);

  // Separate hazard bag notes into discrepancy vs general buckets for clearer presentation.
  const addedNotes = hazardBagNotes?.filter((note) => note.type === "Hazard bag") ?? [];
  const addedDiscrepancies = hazardBagNotes?.filter((note) => note.type === "Specimen Discrepancy") ?? [];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "10px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
          paddingTop: "10px",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "0px 20px" }}>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Hospital</Typography>
                <Typography className={style.value} sx={{ fontWeight: 600 }}>
                  {`${details?.health_organisation?.name ?? ""}`}
                </Typography>
              </Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Patient Name</Typography>
                <Typography className={style.value} sx={{ fontWeight: 600 }}>
                  {`${details?.patient?.surname?.toUpperCase() ?? ""}, ${details?.patient?.given_name ?? ""}`}
                </Typography>
              </Box>
              {details?.patient?.birth_date && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Patient DOB</Typography>
                  <Typography className={style.value} sx={{ fontWeight: 600 }}>
                    {dayjs(details?.patient?.birth_date).format("DD/MM/YYYY")}
                  </Typography>
                </Box>
              )}
              {details?.patient?.mrn_number && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Patient MRN</Typography>
                  <Typography className={style.value} sx={{ fontWeight: 600 }}>
                    {`${details?.patient?.mrn_number?.toUpperCase() ?? ""}`}
                  </Typography>
                </Box>
              )}
              {details?.doctor && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Surgeon</Typography>
                  <Typography className={style.value} sx={{ fontWeight: 600 }}>
                    {`${details?.doctor?.surname?.toUpperCase() ?? ""}, ${details?.doctor?.given_name ?? ""} ${details?.doctor?.prefix ?? ""}`}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                maxHeight: "calc(100vh - 195px)",
                overflow: "auto",
                marginTop: "20px",
              }}
            >
              <CustomTable<HazardBagSpecimen>
                fields={fields}
                paginationRequired={false}
                // count={specimens?.length}
                data={specimens || []}
              // handlePageChange={() => { }}
              />
            </Box>

            {!isEmpty(addedDiscrepancies) && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <Attachment width={24} height={24} />
                  <Typography className={style.detailHeading}>
                    Discrepancies
                  </Typography>
                </Box>
                <Box sx={{ marginBottom: "10px" }}>
                  {addedDiscrepancies.map((note, index) => {
                    const selectedSpecimenDetails = specimens?.filter(
                      (specimen) => specimen?.id === note?.specimen_id
                    );
                    const sutureTimeDetails =
                          (selectedSpecimenDetails[0]?.suture_time ? " " + selectedSpecimenDetails[0]?.suture_time : "") +
                          (selectedSpecimenDetails[0]?.suture_time_edit
                            ? `, ${selectedSpecimenDetails[0]?.suture_time_edit}`
                            : '');
                        const selectedProcedure = referenceData?.procedures?.find((procedure) => procedure?.id === selectedSpecimenDetails[0]?.procedure_id)

                        const procedureDetails =
                          (selectedProcedure?.name ?? '') +
                          (selectedSpecimenDetails[0]?.procedure_edit
                            ? `, ${selectedSpecimenDetails[0]?.procedure_edit}`
                            : '');
                    const createdBy = note?.created_by;
                    return (
                      <Box
                        key={index}
                        sx={{
                          border: "1px solid #a3a3a3",
                          borderRadius: "5px",
                          padding: "10px",
                          marginBottom: "10px",
                          justifyContent: "space-between",
                          flexDirection: "row",
                          display: "flex",
                        }}
                      >
                        <Box sx={{ flex: 2.35 }}>
                          <Typography
                            sx={{
                              fontSize: "16px",
                              fontWeight: "bold",
                              color: colors?.black,
                              textDecorationLine: "underline",
                            }}
                          >{`${procedureDetails}${sutureTimeDetails}, ${selectedSpecimenDetails[0]?.organ ?? ""}${selectedSpecimenDetails[0]?.site ? ", " + selectedSpecimenDetails[0]?.site : ""}`}</Typography>
                          {note?.discrepancy?.description && (
                            <Typography sx={{ fontSize: "16px", color: colors?.black }}>
                              {note?.discrepancy?.description}
                            </Typography>
                          )}
                          {note?.discrepancy_reason?.description && (
                            <Typography sx={{ fontSize: "16px", color: colors?.black }}>
                              {note?.discrepancy_reason?.description}
                            </Typography>
                          )}
                          {note?.notes && (
                            <Typography sx={{ fontSize: "16px", color: colors?.black }}>
                              {note?.notes}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flexDirection: "row", flex: 0.75, marginTop: "5px" }}>
                          <Typography sx={{ color: colors?.black, fontSize: "14px", fontWeight: "600" }}>
                            {createdBy?.role ?? "User"}
                          </Typography>
                          <Typography sx={{ color: colors?.black, fontSize: "14px", fontWeight: "600" }}>
                            {formatDateTime(note?.created_at)}
                          </Typography>
                          <Typography sx={{ color: colors?.black, fontSize: "14px", fontWeight: "600" }}>
                            {formatPersonName(createdBy?.given_name, createdBy?.surname)}
                          </Typography>
                        </Box>
                        <Box sx={{ justifyContent: "flex-end", flex: 0.75, display: "flex", alignItems: "center", marginTop: "5px", marginRight: "5px", gap: 1, flexWrap: "wrap" }}>
                          {(note?.attachments || []).map((att, idx) =>
                            att?.attachment_presigned_url ? (
                              <Box
                                key={`${note.id}-${idx}`}
                                component="img"
                                sx={{
                                  height: 90,
                                  width: 90,
                                  backgroundColor: colors.white,
                                  borderRadius: "5px",
                                  border: "1px solid #a3a3a3",
                                  padding: "1px",
                                  cursor: "pointer",
                                }}
                                src={att.attachment_presigned_url}
                                onClick={() => {
                                  setPreviewVisible(true);
                                  setSelectedImage(att.attachment_presigned_url);
                                }}
                              />
                            ) : null
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {!isEmpty(addedNotes) && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <Attachment width={24} height={24} />
                  <Typography className={style.detailHeading}>Notes</Typography>
                </Box>
                <Box sx={{ marginBottom: "10px" }}>
                  {addedNotes.map((note, index) => {
                    const createdBy = note?.created_by;
                    return (
                      <Box
                        key={index}
                        sx={{
                          border: "1px solid #a3a3a3",
                          borderRadius: "5px",
                          padding: "10px",
                          marginBottom: "10px",
                          justifyContent: "space-between",
                          flexDirection: "row",
                          display: "flex",
                        }}
                      >
                        <Box sx={{ flex: 2.35 }}>
                          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: colors?.black }}>
                            {note?.notes || note?.type || "Note"}
                          </Typography>
                        </Box>
                        <Box sx={{ flexDirection: "row", justifyContent: "space-between", flex: 0.75, marginTop: "5px" }}>
                          <Typography sx={{ color: colors?.black, fontSize: "14px", fontWeight: "600" }}>
                            {createdBy?.role ?? "User"}
                          </Typography>
                          <Typography sx={{ color: colors?.black, fontSize: "14px", fontWeight: "600" }}>
                            {formatDateTime(note?.created_at)}
                          </Typography>
                          <Typography sx={{ color: colors?.black, fontSize: "14px", fontWeight: "600" }}>
                            {formatPersonName(createdBy?.given_name, createdBy?.surname)}
                          </Typography>
                        </Box>
                        <Box sx={{ alignItems: "flex-start", paddingVertical: 7, flex: 0.75 }} />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
                onClick={() => navigate(-1)}
                label={"Close"}
              />
            </Box>
          </Box>
        )}
      </Box>
      {previewVisible && selectedImage && (
        <PreviewDialog
          visible={previewVisible}
          imageUrl={selectedImage}
          onClose={() => {
            setPreviewVisible(false);
            setSelectedImage(undefined);
          }}
        />
      )}
    </Box>
  );
};

export default Details;
